import { useCallback, useRef, useState } from 'react'
import { useAppDispatch } from 'common/hooks/redux'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { procedureApi, useUpdateProcedureMutation } from 'features/Procedure/state/procedure.api'
import {
  getPendingSoapImageUploadCount,
  getSoapNotesScopeKey,
  hasPendingSoapImages,
  registerPendingSoapImageUpload,
  unregisterPendingSoapImageUpload,
} from 'features/Procedure/state/soapImageUpload.helpers'
import type {
  FetchSoapNotesDto,
  ProcedureItem,
  SoapNoteApiItem,
} from 'features/Procedure/state/procedure-api.type'

const OPTIMISTIC_SOAP_NOTE_ID = -1

let nextTempImageId = -1000

const getNextTempImageId = () => {
  const id = nextTempImageId
  nextTempImageId -= 1
  return id
}

const SYNC_MAX_ATTEMPTS = 3
const SYNC_RETRY_DELAY_MS = 750

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

type NotifySyncFailure = () => void

// Re-syncs the cache after the last upload settles; invalidation refetches can be dropped or fail.
const syncSoapNotesAfterUploads = async (
  dispatch: ReturnType<typeof useAppDispatch>,
  queryArg: FetchSoapNotesDto,
  notifySyncFailure: NotifySyncFailure,
): Promise<void> => {
  const scopeKey = getSoapNotesScopeKey(queryArg)
  const syncStartedAt = Date.now()

  for (let attempt = 1; attempt <= SYNC_MAX_ATTEMPTS; attempt += 1) {
    if (getPendingSoapImageUploadCount(scopeKey) > 0) {
      return
    }

    const result = await (dispatch as any)(
      procedureApi.endpoints.fetchSoapNotes.initiate(queryArg, {
        subscribe: false,
        forceRefetch: true,
      }),
    )

    // A deduped initiate resolves with an older in-flight request — only trust fresh successes.
    const isFreshSuccess =
      Boolean(result?.isSuccess) && (result?.startedTimeStamp ?? 0) >= syncStartedAt

    if (isFreshSuccess && !hasPendingSoapImages(result?.data as SoapNoteApiItem[] | undefined)) {
      return
    }

    if (attempt < SYNC_MAX_ATTEMPTS) {
      await delay(SYNC_RETRY_DELAY_MS * attempt)
    }
  }

  notifySyncFailure()
}

type UploadArgs = {
  procedure: ProcedureItem
  imageDataUrl: string
}

type Props = {
  siteId: string | undefined
  selectedPatientId: number | null
  selectedEncounterUid: string | null
}

export const useUploadProcedureSoapImage = ({
  siteId,
  selectedPatientId,
  selectedEncounterUid,
}: Props) => {
  const dispatch = useAppDispatch()
  const { setNotification } = useNotification()
  const [isUploading, setIsUploading] = useState(false)
  const activeUploadsRef = useRef(0)
  const [updateProcedure] = useUpdateProcedureMutation()

  const canUpload = Boolean(siteId && selectedPatientId && selectedEncounterUid)

  const uploadImage = useCallback(
    async ({ procedure, imageDataUrl }: UploadArgs) => {
      if (!siteId || !selectedPatientId || !selectedEncounterUid) {
        return
      }

      activeUploadsRef.current += 1
      setIsUploading(true)

      const tempId = getNextTempImageId()
      const queryArg: FetchSoapNotesDto = {
        patientPid: selectedPatientId,
        encounterUid: selectedEncounterUid,
      }
      const scopeKey = getSoapNotesScopeKey(queryArg)

      // Targeted rollback; undo() is unsafe once a refetch has replaced the cached array.
      const removeOptimisticImage = () => {
        ;(dispatch as any)(
          procedureApi.util.updateQueryData(
            'fetchSoapNotes',
            queryArg,
            (draft: SoapNoteApiItem[]) =>
              draft
                .map((note) => ({
                  ...note,
                  soap_images: (note.soap_images || []).filter((img) => img.id !== tempId),
                }))
                .filter((note) => note.id > 0 || note.soap_images.length > 0),
          ),
        )
      }

      const notifySyncFailure = () => {
        setNotification({
          type: ALERT_CONSTANTS.WARNING,
          duration: 4,
          title: 'Photo list may be outdated',
          description:
            'The photos were uploaded, but the list could not be refreshed. Please reload the page to see all photos.',
        })
      }

      try {
        registerPendingSoapImageUpload(tempId, scopeKey)
        ;(dispatch as any)(
          procedureApi.util.updateQueryData(
            'fetchSoapNotes',
            queryArg,
            (draft: SoapNoteApiItem[]) => {
              if (draft.length === 0) {
                const optimisticNote: SoapNoteApiItem = {
                  id: OPTIMISTIC_SOAP_NOTE_ID,
                  date: new Date().toISOString(),
                  pid: selectedPatientId,
                  user: null,
                  groupname: null,
                  authorized: 1,
                  activity: 1,
                  subjective: null,
                  objective: null,
                  assessment: null,
                  plan: null,
                  soap_images: [],
                }
                draft.unshift(optimisticNote)
              }

              const note = draft[0]
              if (!Array.isArray(note.soap_images)) {
                note.soap_images = []
              }
              note.soap_images.push({
                id: tempId,
                image: imageDataUrl,
                procedure_id: procedure.id,
              })
            },
          ),
        )

        await updateProcedure({
          siteId,
          encounterUid: selectedEncounterUid,
          data: {
            procedures: [
              {
                id: procedure.id,
                code: procedure.code,
                code_type: procedure.code_type,
                procedure_units: procedure.procedure_units,
                units: procedure.units,
                fee: procedure.fee,
                provider_id: procedure.provider_id,
                justify_codes: procedure.justify_codes,
                image: imageDataUrl,
              },
            ],
          },
        }).unwrap()
      } catch (error: any) {
        removeOptimisticImage()
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Unable to upload image',
          description: error?.data?.message || 'Please try again later.',
        })
      } finally {
        unregisterPendingSoapImageUpload(tempId)

        if (getPendingSoapImageUploadCount(scopeKey) === 0) {
          void syncSoapNotesAfterUploads(dispatch, queryArg, notifySyncFailure)
        }

        activeUploadsRef.current -= 1
        setIsUploading(activeUploadsRef.current > 0)
      }
    },
    [siteId, selectedPatientId, selectedEncounterUid, dispatch, updateProcedure, setNotification],
  )

  return {
    uploadImage,
    isUploading,
    canUpload,
  }
}
